// P4-SCHED — architectural activity generator (Release 1, SCHEDULE_INTELLIGENCE
// _MODULE.md §10, §42, §50). Pure: takes the revision's canonical records and
// returns the activities + dependencies the ruleset implies, with every
// quantity traceable to the rooms / elements / openings / wall runs that
// produced it (§36). Nothing is invented: a quantity that cannot be resolved
// is reported in `unscheduled`, and a fixed-duration activity without a
// production standard is generated with no duration ("Duration required").
//
// Quantity sources (all existing, all net — Track D):
//   wall_conditions   drawn linear wall runs: LF × effective height → SF
//                     (the same rows W-1 prices from; a run without a height
//                     is reported, not guessed)
//   element:<basis>   Room Elements resolved through resolveElementQuantity —
//                     floor / ceiling net of back-outs, wall area net of
//                     openings, HEPA / lighting engine effective counts,
//                     perimeter for cove
//   openings:<kind>   placed doors / windows / pass-throughs (a manual count
//                     row counts its QuantityOverride)
//   mep:<what>        Release 2-B — route LF by piping group, refrigerant line
//                     sets, new panels, wired circuits, equipment connections
//                     (src/utils/mepScheduleTakeoff.js; `mep` = the raw records,
//                     omitted = no MEP scope, exactly the Release 1 behavior)
//
// Existing activity rows (by ActivityKey) carry the estimator's inputs — crew
// count, shift overrides, minimums — into the regenerated activity so a
// regeneration never resets what the estimator set (§44).

import { ACTIVITY_TYPES, ACTIVITY_TYPE_BY_KEY, DEPENDENCY_RULES, PUNCH_PREDECESSOR_PHASES, PROFILE_EXCLUSIONS, DEFAULT_SCOPE_PROFILE, phaseOrder, activityKeyFor } from "../config/scheduleActivityTypes";
import { mepTakeoff, mepQuantityFor, EMPTY_MEP_TAKEOFF } from "./mepScheduleTakeoff";
import { resolveElementQuantity, placedCountFor, inferElementBasis, groupElementsByRoom } from "../config/roomElements";
import { calcRoomSystems } from "./roomSystemsEngine";
import { backOutsForElement, isBackOut } from "./takeoffExclusions";
import { openingsForRoom, openingKindOf, isOpening } from "../config/openings";
import { calculateActivityDuration, calendarFromHeader } from "./scheduleEngine";
import { generateProcurementActivities, procurementKey } from "./procurementSchedule";

const r1 = (v) => Math.round(v * 10) / 10;
const parseGeom = (row) => { try { return JSON.parse(row?.GeometryJson || "null"); } catch { return null; } };
const polylineLength = (pts) => { let l = 0; for (let i = 1; i < (pts?.length || 0); i++) l += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y); return l; };

// Wall SF from the drawn linear wall runs: LF (QuantityOverride, else stored
// lengthFt, else the polyline) × height (instance override, else the type's
// default). Rows keyed to both rooms of a shared wall are one run — counted
// once (they are one row).
export function wallConditionTakeoff(takeoffConditions = [], rooms = []) {
  const roomName = (id) => rooms.find(r => r.RoomID === id)?.RoomName || null;
  const detail = [], unsized = [];
  let sf = 0, lf = 0;
  for (const c of takeoffConditions) {
    if (isBackOut(c) || c.Style !== "linear") continue;
    const g = parseGeom(c);
    const length = c.QuantityOverride != null ? +c.QuantityOverride : (g?.lengthFt != null ? +g.lengthFt : (g?.points ? polylineLength(g.points) : null));
    if (!(length > 0)) continue;
    const height = c.HeightFtOverride != null && c.HeightFtOverride !== "" ? +c.HeightFtOverride : (c.DefaultHeightFt != null ? +c.DefaultHeightFt : null);
    lf += length;
    if (!(height > 0)) { unsized.push({ conditionId: c.TakeoffConditionDrawnID, roomId: c.RoomID, roomName: roomName(c.RoomID), lengthFt: r1(length), label: c.Code || c.Name || "wall run" }); continue; }
    const area = length * height;
    sf += area;
    detail.push({ conditionId: c.TakeoffConditionDrawnID, roomId: c.RoomID, roomName: roomName(c.RoomID), label: c.Code || c.Name || "wall run", lengthFt: r1(length), heightFt: height, qty: r1(area) });
  }
  return { sf: r1(sf), lf: r1(lf), detail, unsized };
}

// Openings by kind: { door: { count, detail }, window: …, pass_through: … }.
export function openingTakeoff(takeoffConditions = [], rooms = []) {
  const roomName = (id) => rooms.find(r => r.RoomID === id)?.RoomName || null;
  const out = { door: { count: 0, detail: [] }, window: { count: 0, detail: [] }, pass_through: { count: 0, detail: [] } };
  for (const c of takeoffConditions) {
    if (!isOpening(c)) continue;
    const kind = openingKindOf(c)?.key;
    if (!out[kind]) continue;
    const g = parseGeom(c);
    const n = g && (g.x != null) ? 1 : (c.QuantityOverride != null ? +c.QuantityOverride : 1); // a placed symbol is 1; a manual count row carries its count
    if (!(n > 0)) continue;
    out[kind].count += n;
    out[kind].detail.push({ conditionId: c.TakeoffConditionDrawnID, roomId: c.RoomID, roomName: roomName(c.RoomID), mark: c.Mark || null, qty: n });
  }
  return out;
}

// Room Element quantities by basis: { [basis]: { qty, unit, detail, unresolved } }.
export function elementTakeoff({ rooms = [], roomElements = [], takeoffConditions = [], formulas = [], criteria = {}, systemsCtx = null }) {
  const ctx = systemsCtx || { roomElementsByRoom: groupElementsByRoom(roomElements) };
  const byBasis = {};
  const systemsByRoom = new Map();
  const roomsById = new Map(rooms.map(r => [r.RoomID, r]));
  const unmapped = [];
  for (const el of roomElements) {
    const room = roomsById.get(el.RoomID);
    if (!room) continue;
    const basis = inferElementBasis(el);
    if (!systemsByRoom.has(room.RoomID)) systemsByRoom.set(room.RoomID, calcRoomSystems(room, criteria, ctx));
    const placed = placedCountFor(el, room.RoomID, takeoffConditions);
    const q = resolveElementQuantity({
      element: el, room, systems: systemsByRoom.get(room.RoomID), placed, formulas, criteria, ctx,
      backOuts: backOutsForElement(el, takeoffConditions), openings: openingsForRoom(room, takeoffConditions, rooms),
    });
    if (!ACTIVITY_TYPES.some(t => t.source === `element:${basis}`)) {
      unmapped.push({ roomElementId: el.RoomElementID, roomId: room.RoomID, roomName: room.RoomName, label: el.Name || el.Code, basis, qty: q.effective, unit: q.unit });
      continue;
    }
    const bucket = byBasis[basis] ||= { qty: 0, unit: q.unit, detail: [], unresolved: [] };
    if (q.effective == null || !(q.effective >= 0)) { bucket.unresolved.push({ roomElementId: el.RoomElementID, roomId: room.RoomID, roomName: room.RoomName, label: el.Name || el.Code }); continue; }
    bucket.qty += q.effective;
    bucket.detail.push({ roomElementId: el.RoomElementID, roomId: room.RoomID, roomName: room.RoomName, label: el.Name || el.Code, qty: q.effective, override: q.override, net: q.net ? { gross: q.net.gross, deducted: q.net.totalDeduction } : null });
  }
  for (const b of Object.values(byBasis)) b.qty = r1(b.qty);
  return { byBasis, unmapped };
}

// The generator. Returns { activities, dependencies, unscheduled, warnings, takeoff }.
//   activities   [{ activityKey, origin:'derived', activityType, name, phase,
//                   discipline, isMilestone, sourceQuantityType, sourceQuantity,
//                   sourceUnit, sourceDetail, standardId, standardVersion,
//                   ratePerCrewHour, fixedDurationDays, crewCount, crewSize,
//                   minimumDurationDays, calculatedDurationDays, displayOrder }]
//   dependencies [{ predecessorKey, successorKey, relationshipType, lagDays }]
export function generateArchitecturalActivities({
  rooms = [], roomElements = [], takeoffConditions = [], formulas = [], criteria = {}, systemsCtx = null,
  profile = DEFAULT_SCOPE_PROFILE, standardsByType = {}, header = {}, existingByKey = {},
  mep = null, // Release 2-B: { routeSegments, utilitySystems, scenarios, hvacSystems, panels, circuits, equipment, connections }
}) {
  const cal = calendarFromHeader(header);
  const excluded = PROFILE_EXCLUSIONS[profile?.deliveryModel] || new Set();
  const warnings = [];
  const unscheduled = [];

  const walls = wallConditionTakeoff(takeoffConditions, rooms);
  const openings = openingTakeoff(takeoffConditions, rooms);
  const elements = elementTakeoff({ rooms, roomElements, takeoffConditions, formulas, criteria, systemsCtx });
  for (const u of walls.unsized) unscheduled.push({ code: "wall_height_missing", message: `Wall run ${u.label}${u.roomName ? ` in ${u.roomName}` : ""} (${u.lengthFt} LF) has no height — its SF is not scheduled.`, ...u });
  for (const u of elements.unmapped) unscheduled.push({ code: "element_not_scheduled", message: `${u.label} in ${u.roomName} (${u.qty ?? "—"} ${u.unit}) has no schedule activity type for basis "${u.basis}" yet.`, ...u });
  for (const [basis, b] of Object.entries(elements.byBasis)) for (const u of b.unresolved) unscheduled.push({ code: "element_unresolved", message: `${u.label} in ${u.roomName} has no resolved quantity (${basis}).`, ...u });
  const mepQty = mep ? mepTakeoff({ ...mep, rooms }) : EMPTY_MEP_TAKEOFF;
  if (!excluded.has("MOBILIZATION")) unscheduled.push(...mepQty.unscheduled); // design-only: no MEP installation to report on

  const quantityFor = (type) => {
    if (type.source?.startsWith("mep:")) return mepQuantityFor(mepQty, type.source);
    if (type.source === "wall_conditions") return { qty: walls.sf, detail: walls.detail };
    if (type.source?.startsWith("element:")) { const b = elements.byBasis[type.source.slice(8)]; return b ? { qty: b.qty, detail: b.detail } : null; }
    if (type.source?.startsWith("openings:")) { const o = openings[type.source.slice(9)]; return o && o.count > 0 ? { qty: o.count, detail: o.detail } : null; }
    return null;
  };

  const activities = [];
  // Pass 1 — quantity activities that have scope.
  for (const type of ACTIVITY_TYPES) {
    if (type.kind !== "quantity" || excluded.has(type.key)) continue;
    const q = quantityFor(type);
    if (!q || !(q.qty > 0)) continue;
    activities.push(buildActivity(type, { qty: q.qty, detail: q.detail }, standardsByType, cal, existingByKey));
  }
  const present = new Set(activities.map(a => a.activityType));
  // Pass 2 — fixed activities and milestones (rulesets: `requires` gates them on scope).
  for (const type of ACTIVITY_TYPES) {
    if (type.kind === "quantity" || excluded.has(type.key) || type.perEquipment) continue;
    if (type.requires && !type.requires.some(k => present.has(k))) continue;
    activities.push(buildActivity(type, null, standardsByType, cal, existingByKey));
    present.add(type.key);
  }
  activities.sort((a, b) => phaseOrder(a.phase) - phaseOrder(b.phase) || a.displayOrder - b.displayOrder);
  activities.forEach((a, i) => { a.displayOrder = (i + 1) * 10; });

  // Dependencies — rules where both ends exist, plus every construction
  // install → PUNCH.
  const keys = new Set(activities.map(a => a.activityKey));
  const keyOf = activityKeyFor;
  const dependencies = [];
  const seen = new Set();
  const push = (p, s, type, lag) => {
    const pk = keyOf(p), sk = keyOf(s);
    if (!keys.has(pk) || !keys.has(sk) || pk === sk) return;
    const id = `${pk}>${sk}`;
    if (seen.has(id)) return;
    seen.add(id);
    dependencies.push({ predecessorKey: pk, successorKey: sk, relationshipType: type, lagDays: lag });
  };
  for (const [p, s, type, lag] of DEPENDENCY_RULES) push(p, s, type, lag);
  if (present.has("PUNCH")) for (const a of activities) if (a.kind === "quantity" && PUNCH_PREDECESSOR_PHASES.has(a.phase)) push(a.activityType, "PUNCH", "FS", 0);

  const required = activities.filter(a => a.calculatedDurationDays == null && !a.isMilestone);
  if (required.length) warnings.push({ code: "durations_required", count: required.length, message: `${required.length} activit${required.length === 1 ? "y has" : "ies have"} no production standard yet — enter standards or override their durations.` });
  if (!activities.some(a => a.kind === "quantity")) warnings.push({ code: "no_scope", message: "No scope resolved — add Room Elements, wall runs, openings, utility routes, panels / circuits or equipment connections first." });

  return { activities, dependencies, unscheduled, warnings, takeoff: { walls, openings, elements, mep: mepQty } };
}

function buildActivity(type, q, standardsByType, cal, existingByKey) {
  const key = activityKeyFor(type.key);
  const std = standardsByType[type.key] || null;
  const prior = existingByKey[key] || null;
  const isMilestone = type.kind === "milestone";
  const crewCount = prior?.CrewCount ?? cal.defaultCrewCount ?? 1;
  const minDays = prior?.MinimumDurationDays ?? std?.MinimumDurationDays ?? (isMilestone ? 0 : 1);
  const a = {
    activityKey: key, origin: "derived", activityType: type.key, name: type.name, phase: type.phase, discipline: type.discipline,
    kind: type.kind, isMilestone,
    sourceQuantityType: q ? type.quantityType : null, sourceQuantity: q ? q.qty : null, sourceUnit: q ? type.unit : null, sourceDetail: q ? q.detail : [],
    standardId: std?.StandardID ?? null, standardVersion: std?.Version ?? null,
    ratePerCrewHour: type.kind === "quantity" ? (std?.RatePerCrewHour ?? null) : null,
    fixedDurationDays: type.kind === "fixed" ? (std?.FixedDurationDays ?? null) : null,
    crewCount, crewSize: prior?.CrewSize ?? std?.DefaultCrewSize ?? null,
    shiftsPerDay: prior?.ShiftsPerDay ?? null, hoursPerShift: prior?.HoursPerShift ?? null, overtimeHoursPerShift: prior?.OvertimeHoursPerShift ?? null,
    minimumDurationDays: minDays, displayOrder: 100,
  };
  const calc = calculateActivityDuration({
    kind: type.kind, quantity: a.sourceQuantity, ratePerCrewHour: a.ratePerCrewHour, fixedDurationDays: a.fixedDurationDays,
    crewCount, minimumDurationDays: minDays, calendar: cal,
    activity: { ShiftsPerDay: a.shiftsPerDay, HoursPerShift: a.hoursPerShift, OvertimeHoursPerShift: a.overtimeHoursPerShift },
  });
  a.calculatedDurationDays = calc.calculatedDurationDays;
  a.calc = calc;
  return a;
}

// Index current production standards by ActivityType (one active version each).
export function standardsByActivityType(rows = []) {
  const out = {};
  for (const r of rows) {
    if (r.IsActive === false || r.IsActive === 0) continue;
    if (!out[r.ActivityType] || (r.Version ?? 0) > (out[r.ActivityType].Version ?? 0)) out[r.ActivityType] = r;
  }
  return out;
}

export { ACTIVITY_TYPE_BY_KEY };

// Release 2-A — the whole proposal: architectural activities + one procurement
// chain per equipment record, cross-linked (NTP → submittal, mobilization →
// set, set → punch) where the architectural activities exist. Milestone /
// phase ordering is shared; a `design_only` profile removes procurement too
// (nothing is bought for a design-only contract).
//
// Release 2-B — the MEP records (`mep`) ride along with the architectural
// generator (same ruleset, same dependency rules); the equipment list is shared
// with the procurement chain, and each record's Set / rig precedes the hook-ups
// of that record's own connections (electrical and / or utility).
export function generateScheduleProposal({ equipment = [], mep = null, ...archArgs }) {
  const arch = generateArchitecturalActivities({ ...archArgs, mep: mep ? { ...mep, equipment } : null });
  const excluded = PROFILE_EXCLUSIONS[archArgs.profile?.deliveryModel] || new Set();
  const procurementOff = excluded.has("MOBILIZATION"); // design_only excludes every construction / procurement step
  const archKeys = new Set(arch.activities.map(a => a.activityKey));
  const proc = procurementOff
    ? { activities: [], dependencies: [], warnings: [], unscheduled: [], chains: [] }
    : generateProcurementActivities({ equipment, standardsByType: archArgs.standardsByType, header: archArgs.header, existingByKey: archArgs.existingByKey, archKeys });
  const chainIds = new Set(proc.chains.map(c => c.equipmentId));
  const hookUps = [];
  for (const [side, type] of [["electrical", "ELEC_EQUIP_CONNECTIONS"], ["utility", "UTILITY_EQUIP_CONNECTIONS"]]) {
    const succ = activityKeyFor(type);
    if (!archKeys.has(succ)) continue;
    for (const id of arch.takeoff.mep.connections[side].equipmentIds) if (chainIds.has(id)) hookUps.push({ predecessorKey: procurementKey(id, "SET"), successorKey: succ, relationshipType: "FS", lagDays: 0 });
  }
  const activities = [...arch.activities, ...proc.activities].sort((a, b) => phaseOrder(a.phase) - phaseOrder(b.phase) || a.displayOrder - b.displayOrder);
  activities.forEach((a, i) => { a.displayOrder = (i + 1) * 10; });
  return {
    activities, dependencies: [...arch.dependencies, ...proc.dependencies, ...hookUps],
    unscheduled: [...arch.unscheduled, ...proc.unscheduled], warnings: [...arch.warnings, ...proc.warnings],
    takeoff: arch.takeoff, procurement: { chains: proc.chains },
  };
}
